// La synthèse d'un lead : le récit à gauche (le signal, pourquoi eux,
// pourquoi maintenant, qui contacter), le pilotage à droite (score, axes,
// statut). Plus la navigation entre leads, portée par l'en-tête de la fiche.
import { ChevronLeft, ChevronRight, Clock3, ExternalLink, Target } from "lucide-react";
import {
  ASSESSMENT_AXES,
  LEAD_STATUSES,
  type ContactCandidate,
  type Lead,
  type LeadStatus,
} from "@shared/lib/leads-shared";
import { Button } from "@shared/components/ui/button";
import { cn } from "@shared/lib/utils";
import { ScoreRing } from "@/components/leads-views";
import { OrgLogo } from "@shared/components/org-logo";
import { SourceAnchor } from "@/components/source-anchor";
import { RelativeTime } from "@/components/relative-time";
import { ContactAvatar, ContactLinkedInLink, contactLane } from "@shared/components/contact";
import { useT } from "@shared/client/locale";
import type { Messages } from "@shared/i18n";

export type SheetNav = { index: number; total: number; onPrev: () => void; onNext: () => void };

const STEPS = LEAD_STATUSES.filter((status) => status !== "dismissed");

const hostOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

export function scoreLabel(score: number, labels: Messages["leads"]["overview"]["scoreLabels"]) {
  if (score >= 85) return labels.priority;
  if (score >= 70) return labels.promising;
  return labels.toQualify;
}

/** Précédent / suivant dans la liste filtrée ; ← et → au clavier. */
export function SheetNavigation({ nav }: { nav: SheetNav }) {
  const copy = useT().leads.overview;
  return (
    <span className="inline-flex items-center gap-1 text-[12.5px] text-muted-foreground">
      <Button aria-label={copy.previousLead} disabled={nav.index === 0} onClick={nav.onPrev} size="icon-sm" title={copy.previousTitle} type="button" variant="ghost">
        <ChevronLeft />
      </Button>
      <span className="tabular-nums">
        {nav.index + 1} / {nav.total}
      </span>
      <Button aria-label={copy.nextLead} disabled={nav.index >= nav.total - 1} onClick={nav.onNext} size="icon-sm" title={copy.nextTitle} type="button" variant="ghost">
        <ChevronRight />
      </Button>
    </span>
  );
}

/** Le statut en étapes : on voit où en est le lead, on l'avance en un clic. */
function StatusStepper({ status, onChange }: { status: LeadStatus; onChange?: (status: LeadStatus) => void }) {
  const t = useT();
  const current = (STEPS as readonly LeadStatus[]).indexOf(status);
  return (
    <ol aria-label={t.leads.overview.progress} className="m-0 flex list-none flex-col gap-0.5 p-0">
      {STEPS.map((step, index) => {
        const done = current >= 0 && index <= current;
        const active = index === current;
        return (
          <li key={step}>
            <button
              aria-current={active ? "step" : undefined}
              className={cn(
                "flex w-full cursor-pointer items-center gap-2 rounded-md border-0 bg-transparent px-2 py-1.5 text-[13px] whitespace-nowrap hover:bg-muted disabled:cursor-default",
                active ? "bg-muted font-semibold text-foreground" : done ? "text-foreground" : "text-muted-foreground",
              )}
              disabled={!onChange || active}
              onClick={() => onChange?.(step)}
              type="button"
            >
              <i
                aria-hidden="true"
                className={cn("size-2 flex-none rounded-full border", done ? "border-foreground bg-foreground" : "border-border-strong bg-background")}
              />
              {t.leads.statuses[step]}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/** Le signal, en grand : c'est la raison d'appeler. */
function SignalHero({ lead }: { lead: Lead }) {
  const copy = useT().leads.overview;
  const signal = lead.latest_signal;
  if (!signal) return <p className="m-0 text-[13px] text-muted-foreground">{copy.noSignal}</p>;
  const date = signal.observed_on || null;
  return (
    <section className="grid gap-4">
      <p className="m-0 font-serif text-[22px] leading-[1.35] tracking-[-0.01em] max-sm:text-[19px]">{copy.quoted(signal.market_clue)}</p>
      {signal.source_link && (
        <SourceAnchor
          className="inline-flex w-fit items-center gap-2.5 rounded-lg border px-3 py-2 text-[13px] hover:bg-muted"
          href={signal.source_link}
          label={signal.source_name}
          meta={date}
          quote={signal.source_quote}
        >
          <OrgLogo name={signal.source_name || hostOf(signal.source_link)} size={22} website={signal.source_link} />
          <span className="font-medium">{signal.source_name || hostOf(signal.source_link)}</span>
          {date && <RelativeTime className="text-muted-foreground" value={date} />}
          <ExternalLink aria-hidden="true" className="size-3.5 text-muted-foreground" />
        </SourceAnchor>
      )}
    </section>
  );
}

function WhyGrid({ lead }: { lead: Lead }) {
  const copy = useT().leads.overview;
  const signal = lead.latest_signal;
  if (!signal?.relevance && !signal?.momentum) return null;
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      {signal.relevance && (
        <section className="grid content-start gap-1.5">
          <h3 className="m-0 flex items-center gap-2 text-[13px] font-medium text-muted-foreground">
            <Target aria-hidden="true" className="size-4" /> {copy.whyThem}
          </h3>
          <p className="m-0 text-[14px] leading-[1.55]">{signal.relevance}</p>
        </section>
      )}
      {signal.momentum && (
        <section className="grid content-start gap-1.5">
          <h3 className="m-0 flex items-center gap-2 text-[13px] font-medium text-muted-foreground">
            <Clock3 aria-hidden="true" className="size-4" /> {copy.whyNow}
          </h3>
          <p className="m-0 text-[14px] leading-[1.55]">{signal.momentum}</p>
        </section>
      )}
    </div>
  );
}

/** Les interlocuteurs en bandeau : un clic ouvre la bonne fiche contact. */
function PeopleStrip({
  contacts,
  onOpen,
  stacked = false,
}: {
  contacts: ContactCandidate[];
  onOpen: (id: string) => void;
  stacked?: boolean;
}) {
  const t = useT();
  if (!contacts.length) return null;
  return (
    <section className="grid gap-3">
      <h3 className="m-0 text-[13px] font-medium text-muted-foreground">{t.leads.overview.whoToContact}</h3>
      <div className={cn("grid gap-2", !stacked && "sm:grid-cols-3")}>
        {contacts.slice(0, 3).map((contact) => (
          <div
            className="flex min-w-0 items-center rounded-lg border bg-background hover:border-border-strong hover:bg-muted/40"
            key={contact.id}
          >
            <button
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 p-3 text-left"
              onClick={() => onOpen(contact.id)}
              type="button"
            >
              <ContactAvatar className="size-10" name={contact.contact_name} photo={contact.contact_photo_url} textClassName="text-[13px]" />
              <span className="grid min-w-0 gap-0.5">
                <span className="truncate text-[13.5px] font-medium">{contact.contact_name}</span>
                <span className="truncate text-[12.5px] text-muted-foreground">{contact.contact_role || t.leads.overview.roleToConfirm}</span>
                <span className="text-[12px] text-muted-foreground">{t.leads.contact.lanes[contactLane(contact)]}</span>
              </span>
            </button>
            <ContactLinkedInLink className="mr-2" contact={contact} />
          </div>
        ))}
      </div>
    </section>
  );
}

function Axes({ lead }: { lead: Lead }) {
  const labels = useT().leads.assessmentAxes;
  const assessment = lead.latest_signal?.assessment;
  if (!assessment) return null;
  return (
    <div className="grid gap-2.5">
      {ASSESSMENT_AXES.map((key) => {
        const value = assessment[key];
        if (typeof value !== "number") return null;
        return (
          <div className="grid gap-1" key={key}>
            <div className="flex justify-between text-[12.5px]">
              <span className="text-muted-foreground">{labels[key]}</span>
              <span className="tabular-nums">{value}/5</span>
            </div>
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((step) => (
                <i className={cn("h-1 flex-1 rounded-full", step <= value ? "bg-foreground/80" : "bg-muted")} key={step} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Le récit à gauche, le pilotage (score, axes, statut) à droite. */
export function LeadOverview({
  lead,
  contacts,
  onOpenContact,
  onStatus,
}: {
  lead: Lead;
  contacts: ContactCandidate[];
  onOpenContact: (id: string) => void;
  onStatus?: (status: LeadStatus) => void;
}) {
  const copy = useT().leads.overview;
  const score = lead.latest_signal?.priority ?? lead.osint_score;
  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_260px]">
      <div className="grid content-start gap-8">
        <SignalHero lead={lead} />
        <WhyGrid lead={lead} />
        {lead.latest_signal?.verification_note && (
          <details className="rounded-lg border p-3 text-[13px]">
            <summary className="cursor-pointer font-medium">{copy.toVerify}</summary>
            <p className="mt-2 mb-0 text-muted-foreground">{lead.latest_signal.verification_note}</p>
          </details>
        )}
        <PeopleStrip contacts={contacts} onOpen={onOpenContact} />
        {lead.osint_summary && (
          <section className="grid gap-1.5 border-t pt-6">
            <h3 className="m-0 text-[13px] font-medium text-muted-foreground">{copy.publicResearch}</h3>
            <p className="m-0 text-[14px] leading-[1.55]">{lead.osint_summary}</p>
          </section>
        )}
      </div>
      <aside className="grid content-start gap-6 lg:border-l lg:pl-8">
        {typeof score === "number" && (
          <div className="flex items-center gap-4">
            <ScoreRing score={score} size={64} />
            <span className="grid">
              <span className="text-[15px] font-semibold">{scoreLabel(score, copy.scoreLabels)}</span>
              <span className="text-[12.5px] text-muted-foreground">{copy.scoreOutOf}</span>
            </span>
          </div>
        )}
        <Axes lead={lead} />
        <section className="grid gap-2">
          <h3 className="m-0 text-[13px] font-medium text-muted-foreground">{copy.status}</h3>
          <StatusStepper onChange={onStatus} status={lead.status} />
        </section>
        {lead.latest_signal?.contact_route && (
          <section className="grid gap-1.5">
            <h3 className="m-0 text-[13px] font-medium text-muted-foreground">{copy.approachRoute}</h3>
            <p className="m-0 text-[13px] leading-[1.5]">{lead.latest_signal.contact_route}</p>
          </section>
        )}
      </aside>
    </div>
  );
}
